import { useState, useRef } from "react";
import { useInfiniteQuery, InfiniteQueryConfig } from "react-query";
import { ILookup, ILookupFormattedRow, ILookupRowItem, ILookupWhereField, IValueTypeMap } from "./types";
import { executeLookup } from "./request";
import { transformLookupRowsBySchema } from "./transform-results";
import { ISchemaKey } from "helpers/product-collection";

const PAGE_SIZE = 1000;

/**
 * Helper function to generate cache key for executesql result
 * Keyes by:
 * 1. Merchant id
 * 2. Lookup from
 * 3. Lookup select keys
 * 4. Lookup where and values
 *
 * We don't worry about result limit and offset since this is specifically used with `useInfiniteQuery`
 * @param lookupPlan
 * @param dependenciesValues
 * @param merchantId
 * @returns
 */
const generateQueryCacheKey = (lookupPlan: ILookup, dependenciesValues: IValueTypeMap, merchantId: string) => {
    const keys = [merchantId];

    if (lookupPlan) {
        keys.push(lookupPlan.from.database_name);

        lookupPlan.select.terms.forEach((term) => {
            keys.push(term.column.table_column);
        });

        lookupPlan.select.terms.forEach((term) => {
            keys.push(term.column.table_column);
        });

        lookupPlan.where?.fields.forEach((field) => {
            const value = dependenciesValues[field.column.schema_column]?.value;
            if (value) {
                keys.push(`${field.column.schema_column}_${value}`);
            }
        });
    }

    return keys.join("|");
};

/**
 * Validate field if it's required. If it's required, validate if field is populated
 * @param whereFields WHERE terms
 * @param dependenciesValues values of WHERE conditions, key-ed by schema field name
 * @returns
 */
export const validateWhereRequiredFields = (whereFields: ILookupWhereField[], dependenciesValues: IValueTypeMap) => {
    return whereFields.reduce((acc, whereField) => {
        if (whereField.is_required) {
            const isFieldEmpty =
                // Makes sure fields is not empty
                !String(dependenciesValues[whereField.column.schema_column]?.value ?? "").trim() &&
                // If field is empty, last attemp, check if `enforced_value` is not empty
                !String(dependenciesValues[whereField.column.enforced_value]?.value ?? "").trim();

            isFieldEmpty && acc.push(whereField.column.schema_column);
        }
        return acc;
    }, []);
};

/**
 * Fetch lookup plan
 * @param lookupPlan lookup plan to fetch by
 * @param dependenciesValues values of WHERE conditions, key-ed by schema field name
 * @param merchantId
 * @param options
 * @returns
 */
export const useMerchantLookup = (
    lookupPlan: ILookup,
    dependenciesValues: IValueTypeMap,
    merchantId: string,
    options?: InfiniteQueryConfig<ILookupRowItem[], Error>
) => {
    return useInfiniteQuery<ILookupRowItem[]>(
        generateQueryCacheKey(lookupPlan, dependenciesValues, merchantId),
        (_cacheKey, offset) => {
            return executeLookup(lookupPlan, dependenciesValues, { merchantId, limit: PAGE_SIZE, offset: offset ?? 0 });
        },
        {
            // Since response almost never changes until users refresh assortment, it's safe to make cache never expires and stales
            cacheTime: Infinity,
            staleTime: Infinity,
            ...(options || {}),
            getFetchMore: (lastResponse, allResponses) => {
                const allRowCount = allResponses.reduce((acc, response) => acc + response.length, 0);
                return lastResponse.length >= PAGE_SIZE ? allRowCount : null;
            },
        }
    );
};

/**
 * Fetch lookup plan based on schema
 * @param schema schema to fetch lookup plan
 * @param dependenciesValues values of WHERE conditions, key-ed by schema field name
 * @param merchantId
 * @returns
 */
export const useFetchSchemaLookup = (schema: ISchemaKey, dependenciesValues: IValueTypeMap, merchantId: string) => {
    const hasLookupPlan = !!schema.lookup_plan;
    const isRequiredFieldsPopulated =
        hasLookupPlan && validateWhereRequiredFields(schema.lookup_plan.where.fields, dependenciesValues).length === 0;

    const { data, fetchMore, isInitialData, canFetchMore, isFetchingMore, isFetched } = useMerchantLookup(
        schema.lookup_plan,
        dependenciesValues,
        merchantId,
        {
            enabled: isRequiredFieldsPopulated,
        }
    );

    const lastCompleteData = useRef(data);
    const [rows, setRows] = useState<ILookupFormattedRow[]>([]);

    // reseting hook state whenever it receives a different schema that has no lookup_plan
    if (!schema?.lookup_plan && rows.length > 0) {
        setRows([]);
        lastCompleteData.current = [];
    }

    // `lastCompleteData.current` is results of infinite fetch from React query. It's an array inside an array.
    const latestRequestHasResults = lastCompleteData.current?.length > 0 && lastCompleteData.current[0].length > 0;

    if (canFetchMore && !isFetchingMore) {
        fetchMore();
    }

    if (
        !canFetchMore &&
        !isInitialData &&
        isFetched &&
        // This is to prevent re-render in the future caused by careless dependency shallow-equality mismatch.
        lastCompleteData.current !== data
    ) {
        lastCompleteData.current = data;

        const transformRowsBySchema = transformLookupRowsBySchema(
            schema,
            data.reduce((acc, pageData) => acc.concat(pageData), [])
        );
        setRows(transformRowsBySchema);
    } else if (rows.length === 0 && latestRequestHasResults) {
        // Handle case where `rows` is empty and last lookup returns results
        const transformRowsBySchema = transformLookupRowsBySchema(
            schema,
            data.reduce((acc, pageData) => acc.concat(pageData), [])
        );
        setRows(transformRowsBySchema);
    }

    return {
        rows,
    };
};
